import { useMemo, useState, useEffect } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { MessageCircle, TrendingUp } from "lucide-react";
import { formatBRL, waLink } from "../config";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const terms = [12, 24, 36, 48, 60];

export const Simulator = ({ vehicle }) => {
  const [price, setPrice] = useState(120000);
  const [downPct, setDownPct] = useState(30);
  const [term, setTerm] = useState(48);
  const [config, setConfig] = useState({ taxa_am: 1.49, tac: 1200, iof_fixo_pct: 0.38, iof_dia_pct: 0.0082 });

  useEffect(() => {
    axios.get(`${API}/config/simulador`)
      .then((res) => setConfig(res.data))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (vehicle) setPrice(vehicle.preco);
  }, [vehicle]);

  const { down, financed, tac, iof, base, installment, totalParcelas, jurosTotais, totalPago } = useMemo(() => {
    const down = price * (downPct / 100);
    const financed = Math.max(0, price - down);
    const tac = config.tac;
    const baseIof = financed + tac;
    const dias = Math.min(term * 30, 365);
    const iof = baseIof * (config.iof_fixo_pct / 100) + baseIof * dias * (config.iof_dia_pct / 100);
    const base = baseIof + iof;
    const r = config.taxa_am / 100;
    const installment = (base * r) / (1 - Math.pow(1 + r, -term));
    const totalParcelas = installment * term;
    const jurosTotais = totalParcelas - base;
    const totalPago = down + totalParcelas;
    return { down, financed, tac, iof, base, installment, totalParcelas, jurosTotais, totalPago };
  }, [price, downPct, term, config]);

  const saveSimulationLead = async () => {
    try {
      await axios.post(`${API}/leads`, {
        nome: "Simulação via site",
        whatsapp: "a-definir",
        interesse: "Simulação de financiamento",
        mensagem: `Simulou ${vehicle ? vehicle.nome : "veículo"} de ${formatBRL(price)} | entrada ${formatBRL(down)} | ${term}x de ${formatBRL(installment)}`,
        origem: "simulador",
      });
      toast.success("Simulação registrada! Nossa equipe vai te chamar.");
    } catch {
      toast.error("Não foi possível registrar a simulação.");
    }
  };

  const waMessage = `Olá! Fiz uma simulação no site da Seed Motors: ${vehicle ? vehicle.nome : "veículo"} de ${formatBRL(price)}, entrada de ${formatBRL(down)}, ${term}x de ${formatBRL(installment)} (total ${formatBRL(totalPago)}). Quero uma proposta!`;

  return (
    <section id="simulador" className="py-24 sm:py-32 relative overflow-hidden" data-testid="simulator-section">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(16,185,129,0.08),transparent_60%)]" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8 }}
          className="mb-12"
        >
          <p className="text-xs font-mono2 tracking-widest uppercase text-emerald-400 mb-3">03 — Simulador</p>
          <h2 className="font-display text-3xl sm:text-5xl font-bold tracking-tight text-white">
            Financie em <span className="text-emerald-400">segundos</span>
          </h2>
          <p className="mt-4 text-slate-400 max-w-xl">
            {vehicle
              ? `Simulando: ${vehicle.nome} — ajuste a entrada e o prazo.`
              : "Ajuste o valor, a entrada e o prazo. A parcela aparece na hora, sem cadastro."}
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-5 gap-6">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="lg:col-span-3 glass rounded-3xl p-8 sm:p-10 space-y-10"
          >
            <div>
              <div className="flex justify-between items-baseline mb-4">
                <label className="text-sm font-medium text-slate-300">Valor do veículo</label>
                <span className="font-display text-xl font-bold text-white" data-testid="sim-price-value">{formatBRL(price)}</span>
              </div>
              <input
                type="range" min="50000" max="2500000" step="5000" value={price}
                onChange={(e) => setPrice(Number(e.target.value))}
                style={{ "--fill": `${((price - 50000) / 2450000) * 100}%` }}
                className="w-full" data-testid="sim-price-slider"
              />
            </div>

            <div>
              <div className="flex justify-between items-baseline mb-4">
                <label className="text-sm font-medium text-slate-300">Entrada ({downPct}%)</label>
                <span className="font-display text-xl font-bold text-white" data-testid="sim-down-value">{formatBRL(down)}</span>
              </div>
              <input
                type="range" min="10" max="80" step="5" value={downPct}
                onChange={(e) => setDownPct(Number(e.target.value))}
                style={{ "--fill": `${((downPct - 10) / 70) * 100}%` }}
                className="w-full" data-testid="sim-down-slider"
              />
            </div>

            <div>
              <label className="text-sm font-medium text-slate-300 block mb-4">Prazo</label>
              <div className="flex flex-wrap gap-2" data-testid="sim-terms">
                {terms.map((t) => (
                  <button
                    key={t}
                    onClick={() => setTerm(t)}
                    data-testid={`sim-term-${t}`}
                    className={`px-6 py-2.5 rounded-full text-sm font-semibold transition-colors duration-300 ${
                      term === t ? "bg-emerald-500 text-emerald-950" : "glass text-slate-300 hover:text-emerald-400"
                    }`}
                  >
                    {t}x
                  </button>
                ))}
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.15 }}
            className="lg:col-span-2 rounded-3xl p-8 sm:p-10 bg-gradient-to-b from-emerald-500/15 to-[#131620] border border-emerald-500/30 flex flex-col justify-between"
            data-testid="sim-result-card"
          >
            <div>
              <p className="text-xs font-mono2 tracking-widest uppercase text-emerald-400 flex items-center gap-2">
                <TrendingUp className="w-4 h-4" /> Parcela estimada
              </p>
              <p className="mt-4 font-display text-5xl sm:text-6xl font-extrabold text-white tracking-tight" data-testid="sim-installment">
                {formatBRL(installment)}
              </p>
              <p className="text-slate-400 text-sm mt-1">por mês em {term}x</p>
              <div className="mt-8 space-y-3 text-sm" data-testid="sim-breakdown">
                <div className="flex justify-between text-slate-300">
                  <span>Valor financiado</span>
                  <span className="font-semibold text-white" data-testid="sim-financed">{formatBRL(financed)}</span>
                </div>
                <div className="flex justify-between text-slate-300">
                  <span>Taxa de contrato (TAC)</span>
                  <span className="font-semibold text-white" data-testid="sim-tac">{formatBRL(tac)}</span>
                </div>
                <div className="flex justify-between text-slate-300">
                  <span>IOF</span>
                  <span className="font-semibold text-white" data-testid="sim-iof">{formatBRL(iof)}</span>
                </div>
                <div className="flex justify-between text-slate-300">
                  <span>Taxa de juros</span>
                  <span className="font-semibold text-white" data-testid="sim-taxa">{String(config.taxa_am).replace(".", ",")}% a.m.</span>
                </div>
                <div className="flex justify-between text-slate-300">
                  <span>Juros no período</span>
                  <span className="font-semibold text-white" data-testid="sim-juros">{formatBRL(jurosTotais)}</span>
                </div>
                <div className="border-t border-emerald-500/20 pt-3 flex justify-between text-slate-300">
                  <span>Total pago no final (entrada + parcelas)</span>
                  <span className="font-bold text-emerald-400" data-testid="sim-total">{formatBRL(totalPago)}</span>
                </div>
              </div>
              <p className="mt-4 text-[11px] text-slate-500 leading-relaxed">
                Simulação estimada com TAC e IOF financiados junto ao contrato. Valores sujeitos a análise de crédito e condições do banco parceiro.
              </p>
            </div>
            <div className="mt-10 space-y-3">
              <a
                href={waLink(waMessage)}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="sim-whatsapp-button"
                className="w-full inline-flex items-center justify-center gap-2 bg-emerald-500 hover:bg-emerald-400 text-emerald-950 font-bold px-6 py-4 rounded-full transition-colors duration-300"
              >
                <MessageCircle className="w-5 h-5" />
                Pré-aprovação no WhatsApp
              </a>
              <button
                onClick={saveSimulationLead}
                data-testid="sim-save-button"
                className="w-full glass hover:border-emerald-500/50 text-white font-semibold px-6 py-3.5 rounded-full transition-colors duration-300"
              >
                Quero que me liguem
              </button>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
